"use client";

import { Notice, StatCard, StatGrid } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  Explanation,
  Formula,
  PairedDataInput,
  ResultTable,
  usePairedData,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type CovarianceReport,
  covarianceReport,
} from "@/lib/tool-kit/stats-descriptive/covariance";
import { formatStat } from "@/lib/tool-kit/stats-descriptive/format";

/** English strings. A translated content file overrides these through `ui`. */
export const COVARIANCE_CALCULATOR_STRINGS = {
  entryLabel: "Data entry",
  entryLists: "Separate X and Y lists",
  entryLines: 'Pairs — one "x, y" per line',
  xLabel: "X values",
  yLabel: "Y values",
  xPlaceholder: "2, 4, 6, 8, 10",
  yPlaceholder: "65, 70, 72, 81, 87",
  pairsLabel: "Paired data — one x, y pair per line",
  pairsPlaceholder: "2, 65\n4, 70\n6, 72",
  valuesCount: "one: value|other: values",
  pairsCount: "one: pair read|other: pairs read",
  skippedCount:
    "one: , {count} line without two numbers skipped|other: , {count} lines without two numbers skipped",
  tryExample: "Try an example",
  exampleX: "2, 4, 6, 8, 10",
  exampleY: "65, 70, 72, 81, 87",
  examplePairs: "2, 65\n4, 70\n6, 72\n8, 81\n10, 87",
  clear: "Clear",
  mismatch:
    "The two lists differ in length (X: {x}, Y: {y}). Each x needs a matching y.",
  enterPrompt: "Enter at least two (x, y) pairs to compute the covariance.",
  results: "Results",
  sampleCovariance: "Sample covariance (n − 1)",
  populationCovariance: "Population covariance (n)",
  pairsN: "Pairs (n)",
  correlation: "Pearson's r",
  directionTitle: "What the sign means",
  positive:
    "Positive: above-average x values tend to come with above-average y values.",
  negative:
    "Negative: above-average x values tend to come with below-average y values.",
  zero: "Zero: no linear tendency for x and y to move together.",
  tableTitle: "Deviation products",
  colIndex: "#",
  colX: "x",
  colY: "y",
  colDx: "x − x̄",
  colDy: "y − ȳ",
  colProduct: "(x − x̄)(y − ȳ)",
  sum: "Sum",
  workingTitle: "How it was calculated",
  meansLine: "x̄ = {meanX}, ȳ = {meanY}, n = {n}",
  sampleLine:
    "Sample covariance = Σ(x − x̄)(y − ȳ) ÷ (n − 1) = {sum} ÷ {df} = {cov}",
  populationLine:
    "Population covariance = Σ(x − x̄)(y − ȳ) ÷ n = {sum} ÷ {n} = {cov}",
  correlationLine:
    "r = sample covariance ÷ (s(x) × s(y)) = {cov} ÷ ({sx} × {sy}) = {r}",
  noCorrelation: "r is undefined: one of the variables does not vary.",
  scaleNote:
    "Covariance is in units of x times units of y, so its size depends on the measurement scale: record x in units ten times smaller and the covariance grows tenfold, while r does not change.",
} as const;

type Strings = typeof COVARIANCE_CALCULATOR_STRINGS;

function directionText(cov: number, strings: Strings): string {
  if (cov > 0) {
    return strings.positive;
  }
  if (cov < 0) {
    return strings.negative;
  }
  return strings.zero;
}

function Working({
  report,
  strings,
  num,
}: {
  report: CovarianceReport;
  strings: Strings;
  num: (value: number) => string;
}) {
  const sum = num(report.sumProducts);
  return (
    <WorkingPanel title={strings.workingTitle}>
      <Formula>
        {t(strings.meansLine, {
          meanX: num(report.meanX),
          meanY: num(report.meanY),
          n: num(report.n),
        })}
      </Formula>
      <Formula>
        {t(strings.sampleLine, {
          sum,
          df: num(report.n - 1),
          cov: num(report.sample),
        })}
      </Formula>
      <Formula>
        {t(strings.populationLine, {
          sum,
          n: num(report.n),
          cov: num(report.population),
        })}
      </Formula>
      <Formula>
        {Number.isNaN(report.r)
          ? strings.noCorrelation
          : t(strings.correlationLine, {
              cov: num(report.sample),
              sx: num(report.sdX),
              sy: num(report.sdY),
              r: num(report.r),
            })}
      </Formula>
      <Explanation>{strings.scaleNote}</Explanation>
    </WorkingPanel>
  );
}

export default function CovarianceCalculatorWidget() {
  const strings = useToolUi(COVARIANCE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const data = usePairedData();
  const num = (value: number) => formatStat(fmt, value);

  const report = data.mismatched ? null : covarianceReport(data.xs, data.ys);
  const showMismatch =
    data.mismatched && data.listXCount > 0 && data.listYCount > 0;
  const count = (n: number) => (
    <>
      {fmt(n)}
      {tPlural(strings.valuesCount, locale, n)}
    </>
  );

  return (
    <>
      <section className="space-y-4">
        <PairedDataInput
          data={data}
          idPrefix="cov"
          labels={{
            mode: strings.entryLabel,
            lists: strings.entryLists,
            lines: strings.entryLines,
            x: strings.xLabel,
            y: strings.yLabel,
            xPlaceholder: strings.xPlaceholder,
            yPlaceholder: strings.yPlaceholder,
            pairs: strings.pairsLabel,
            pairsPlaceholder: strings.pairsPlaceholder,
          }}
          pairsHint={
            <>
              {fmt(data.pairCount)}
              {tPlural(strings.pairsCount, locale, data.pairCount)}
              {data.skippedLines > 0
                ? tPlural(strings.skippedCount, locale, data.skippedLines, {
                    count: fmt(data.skippedLines),
                  })
                : null}
            </>
          }
          xHint={count(data.listXCount)}
          yHint={count(data.listYCount)}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() =>
              data.fill({
                x: strings.exampleX,
                y: strings.exampleY,
                pairs: strings.examplePairs,
              })
            }
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={data.clear}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {showMismatch ? (
        <Notice>
          {t(strings.mismatch, {
            x: fmt(data.listXCount),
            y: fmt(data.listYCount),
          })}
        </Notice>
      ) : null}

      {report ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.sampleCovariance}
                value={num(report.sample)}
              />
              <StatCard
                label={strings.populationCovariance}
                value={num(report.population)}
              />
              <StatCard label={strings.pairsN} value={fmt(report.n)} />
              <StatCard label={strings.correlation} value={num(report.r)} />
            </StatGrid>
          </div>
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.directionTitle}</h3>
            <Explanation>{directionText(report.sample, strings)}</Explanation>
          </div>
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.tableTitle}</h3>
            <ResultTable
              footer={[strings.sum, "", "", "", "", num(report.sumProducts)]}
              head={[
                strings.colIndex,
                strings.colX,
                strings.colY,
                strings.colDx,
                strings.colDy,
                strings.colProduct,
              ]}
              rows={report.rows.map((row, index) => [
                fmt(index + 1),
                num(row.x),
                num(row.y),
                num(row.dx),
                num(row.dy),
                num(row.product),
              ])}
            />
          </div>
          <Working num={num} report={report} strings={strings} />
        </section>
      ) : null}
      {report || showMismatch ? null : <Notice>{strings.enterPrompt}</Notice>}
    </>
  );
}
